import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { deleteUser, getUsers } from "../api";
import {
  ActionIcon,
  Badge,
  Container,
  Loader,
  Pagination,
  Table,
  Text,
  Title,
} from "@mantine/core";
import openDeleteModal from "../_Shared/ConfirmDelete";
import { IconCheck, IconTrash, IconX } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";

const formatRole = (role) =>
  role?.replace(/([A-Z])/g, " $1").trim() || role;

const formatTimestamp = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const truncateId = (id) => id?.slice(0, 8) + "…" || "—";

export default function AdminUsersList() {
  const { t } = useTranslation();
  const [users, setUsers] = useState(null);
  const [, setLocation] = useLocation();

  const [activePage, setActivePage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const pageSize = 50;

  useEffect(() => {
    setIsLoading(true);
    getUsers(activePage, pageSize)
      .then((data) => {
        setUsers(data);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [activePage]);

  if (!users) {
    return <Loader size="xl" />;
  }

  return (
    <Container size="xl">
      <Title order={1}>Users</Title>

      <Pagination
        hideWithOnePage={false}
        total={Math.max(1, Math.ceil(users.totalCount / pageSize))}
        value={activePage}
        onChange={setActivePage}
      />
      {isLoading ? <Loader size="sm" /> : null}

      <Table highlightOnHover stickyHeader stickyHeaderOffset={60}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Tenant</Table.Th>
            <Table.Th>Email</Table.Th>
            <Table.Th>First Name</Table.Th>
            <Table.Th>Last Name</Table.Th>
            <Table.Th>Active</Table.Th>
            <Table.Th>Created</Table.Th>
            <Table.Th>{t("generic.actions", "Actions")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {users.data?.map((p) => (
            <Table.Tr
              key={p.id}
              onClick={() => setLocation(`/admin/users/${p.id}`)}
              style={{ cursor: "pointer" }}
            >
              <Table.Td>
                <Badge size="sm" variant="light">{formatRole(p.role)}</Badge>
                <br />
                <Text
                  size="xs"
                  c="dimmed"
                  component={Link}
                  onClick={(evt) => evt.stopPropagation()}
                  to={`/tenants/${p.tenantId}`}
                  title={p.tenantId}
                >
                  {truncateId(p.tenantId)}
                </Text>
              </Table.Td>

              <Table.Td>{p.emailAddress}</Table.Td>
              <Table.Td>{p.firstName}</Table.Td>
              <Table.Td>{p.lastName}</Table.Td>
              <Table.Td>{p.isActive ? <IconCheck size={16} color="green" /> : <IconX size={16} color="gray" />}</Table.Td>
              <Table.Td>{formatTimestamp(p.created)}</Table.Td>
              <Table.Td>
                <ActionIcon
                  size="sm"
                  variant="outline"
                  color="red"
                  onClick={async (evt) => {
                    evt.stopPropagation();
                    openDeleteModal(() => {
                      deleteUser(p.id)
                        .then(() => {
                          setUsers(users.filter((u) => u.id !== p.id));
                        })
                        .catch((err) => {
                          notifications.show({
                            title: t("generic.error"),
                            message: "" + err,
                            color: "red",
                          });
                        });
                    });
                  }}
                >
                  <IconTrash size={14} />
                </ActionIcon>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Container>
  );
}
